/**
 * What the board says about the ring under „Maß“ (spec 2026-10-15 §2): does
 * it close, and — where exactly one wall on an axis has no measure — how much
 * that axis is short.
 *
 * Everything here reads figures the server already decided: the lengths and
 * their sources off `GET …/plan`, the two sums off the outline. Nothing is
 * written anywhere from it.
 */
import { formatMm } from "@/lib/format";
import { carriesMeasure, isFase, type DemoOutline } from "@/lib/outline";

export type Axis = "h" | "v";

/** One wall as the axis hint needs it: which way it runs and what it carries. */
export interface DirectedWall {
  /** `rechts` · `links` · `runter` · `hoch` — the sheet directions of the
   * backend (`schema/facade.py`, `_DIRECTIONS`; y grows downwards). `""`
   * for a step that runs along neither axis. */
  direction: string;
  lengthMm: number | null;
  lengthSource?: string;
}

/** Plus or minus on its axis, per direction — `_DIRECTIONS` read sideways. */
const SIGN: Record<string, { axis: Axis; sign: 1 | -1 }> = {
  rechts: { axis: "h", sign: 1 },
  links: { axis: "h", sign: -1 },
  runter: { axis: "v", sign: 1 },
  hoch: { axis: "v", sign: -1 },
};

export function axisOf(direction: string): Axis | null {
  return SIGN[direction]?.axis ?? null;
}

/**
 * Each wall of the outline with the direction it is drawn in.
 *
 * The plan answer names no direction per wall (`SegmentOut` carries none), so
 * it is read off the nodes the wall runs between: wall *i* goes from node *i*
 * to node *i + 1* (`OutlineCandidate.segments`' own invariant). Only the sign
 * of the step is used — never its drawn length, which is geometry and not a
 * measure (A-014).
 */
export function directedWalls(outline: DemoOutline): DirectedWall[] {
  return outline.segments.flatMap((segment, index) => {
    if (isFase(segment)) return chamferLegs(outline, index);
    return [
      {
        direction: directionOf(outline, index),
        lengthMm: segment.lengthMm,
        lengthSource: segment.lengthSource,
      },
    ];
  });
}

/**
 * Which way wall `index` is drawn — `rechts` · `links` · `runter` · `hoch`,
 * `""` for a step along neither axis (a chamfer's slant among them). The one
 * reading of a single wall's direction, for a caller that asks about the wall
 * it has picked: {@link directedWalls} is no longer one entry per wall once a
 * chamfer splits its slant in two.
 */
export function directionOf(outline: DemoOutline, index: number): string {
  const from = outline.nodes[index];
  const to = outline.nodes[index + 1];
  if (from === undefined || to === undefined) return "";
  const dx = to.xMm - from.xMm;
  const dy = to.yMm - from.yMm;
  if (dy === 0 && dx !== 0) return dx > 0 ? "rechts" : "links";
  if (dx === 0 && dy !== 0) return dy > 0 ? "runter" : "hoch";
  return "";
}

/**
 * A chamfer's slant as the two legs it stands for — one on each axis
 * (design 2026-10-18 §6.5): the ring under the chamfer is still the
 * axis-parallel one the closure sums are written on, and the shortened walls
 * beside the slant carry only their usable stretch. Their usable stretch and
 * the leg on the same axis add up to the customer's measure again, so each
 * axis sums exactly what it summed before the chamfer was cut.
 *
 * The legs are read off the slant's two nodes, which the server put there
 * from the two legs a person typed — in whole millimetres, the same figures,
 * not a drawn distance (A-014). They carry the slant's own source.
 */
function chamferLegs(outline: DemoOutline, index: number): DirectedWall[] {
  const segment = outline.segments[index]!;
  const from = outline.nodes[index];
  const to = outline.nodes[index + 1];
  if (from === undefined || to === undefined) {
    return [{ direction: "", lengthMm: segment.lengthMm, lengthSource: segment.lengthSource }];
  }
  const dx = to.xMm - from.xMm;
  const dy = to.yMm - from.yMm;
  const carried = carriesMeasure(segment);
  return [
    {
      direction: dx > 0 ? "rechts" : "links",
      lengthMm: carried ? Math.abs(dx) : null,
      lengthSource: segment.lengthSource,
    },
    {
      direction: dy > 0 ? "runter" : "hoch",
      lengthMm: carried ? Math.abs(dy) : null,
      lengthSource: segment.lengthSource,
    },
  ];
}

/**
 * How far the measured walls on one axis leave it short — where exactly one
 * wall on that axis carries no measure and every other one does. `null`
 * otherwise: with two walls missing, the rest could be split any way, and
 * with none missing there is no gap to name, only a closure to check.
 *
 * **A named exception** to the board's own rule „Nothing in this file works
 * out a domain figure“ (`PlanningScreen.tsx`, „Asking for a plan“): this is a
 * sum, worked out here. It is allowed because it is shown and never written —
 * the hint „Auf dieser Achse fehlen … mm.“ is text beside the field, **never
 * a value put into it** (spec §1, E-13: a remainder taken over closes the
 * ring by construction and hides the very misreading the ring exists to
 * show). Whoever means that figure types it.
 *
 * Any wall the outline cannot place on an axis makes the whole answer `null`:
 * a sum over an axis that does not know all its walls is not a sum.
 */
export function axisGap(segments: readonly DirectedWall[], axis: Axis): number | null {
  if (segments.some((wall) => axisOf(wall.direction) === null)) return null;
  const onAxis = segments.filter((wall) => axisOf(wall.direction) === axis);
  const missing = onAxis.filter((wall) => !carriesMeasure(wall));
  if (missing.length !== 1) return null;
  const sum = onAxis
    .filter((wall) => carriesMeasure(wall))
    .reduce((total, wall) => total + SIGN[wall.direction]!.sign * wall.lengthMm!, 0);
  return Math.abs(sum);
}

/**
 * The ring under the field, in one sentence — or `null` for an arrangement
 * that is no chain at all.
 *
 * Read in this order, and the order is the point:
 *
 * 1. **An open outline** (`closureEvaluable === false`) has nothing to close
 *    (E-14/E-15).
 * 2. **A wall without a measure** makes the check wait — *before* the sums are
 *    looked at. On a ring the backend forces such a wall to whatever closes
 *    it (`_close_ring`, source `closure`), so its sums read zero by
 *    construction; „Umlauf schließt“ there would be the one false thing this
 *    line could say (E-13).
 * 3. **Every wall measured:** the sums are the measures' own, and zero on both
 *    axes is a ring that closes. Otherwise the open amount per axis, the axis
 *    that closes left out.
 */
export function closureSentence(outline: DemoOutline): string | null {
  if (!outline.connected || outline.segments.length === 0) return null;
  if (outline.closureEvaluable === false) return "Offener Umriss — keine Schließungsprüfung.";
  if (!outline.segments.every((segment) => carriesMeasure(segment))) {
    return "Schließung erst prüfbar, wenn alle Wände ein Maß tragen.";
  }
  const h = Math.abs(outline.sumHorizontalMm);
  const v = Math.abs(outline.sumVerticalMm);
  if (h === 0 && v === 0) return "Umlauf schließt.";
  const open = [
    h === 0 ? null : `${formatMm(h)} waagerecht`,
    v === 0 ? null : `${formatMm(v)} senkrecht`,
  ].filter((part): part is string => part !== null);
  return `Umlauf offen um ${open.join(" · ")}.`;
}
